'use client';

/**
 * Delegation-aware edge variant used by `WorkspaceCanvas` whenever a
 * provenance edge connects a parent agent to a delegation puck
 * (`cache.relation === 'delegation'`).
 *
 * Visual design:
 *   - Bezier path between source/target handles, matching the rest of
 *     the provenance-edge geometry so layout reads consistently.
 *   - Two stacked `<path>`s on the SVG:
 *       1. Static stroke — always visible; carries hover hit area and
 *          the base colour. Fades to a muted trace during
 *          `in_progress` so the moving dots above read as the
 *          dominant visual.
 *       2. Animated overlay — rendered only while `data.status ===
 *          'in_progress'`. Uses `stroke-linecap: round` plus a
 *          `stroke-dasharray` of `0 <gap>` so each "dash" collapses
 *          into a single round dot. A CSS keyframe drives
 *          `stroke-dashoffset` from `0` to `-<gap>`, making the dots
 *          appear to travel from source to target. When status flips
 *          to `complete` / `error` the overlay unmounts — no
 *          animation-cleanup gymnastics.
 *   - Status colour driven by a CSS variable on the wrapping `<g>`:
 *       in-progress = purple (`--ws-delegation`)
 *       complete    = muted purple (50% opacity)
 *       error       = rose
 *
 * We deliberately use CSS dash-offset rather than SMIL `<animateMotion>`
 * — SMIL is patchy across browsers (Safari quirks, Firefox jank under
 * React re-renders of the `d` attribute), and the CSS approach is GPU-
 * accelerated and survives every path mutation.
 */

import { memo } from 'react';
import { BaseEdge, getBezierPath, type EdgeProps } from '@xyflow/react';

type DelegationStatus = 'in_progress' | 'complete' | 'error';

interface DelegationEdgeData extends Record<string, unknown> {
  status?: DelegationStatus;
}

function DelegationEdgeImpl(props: EdgeProps) {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style,
    markerEnd,
    data,
  } = props;
  const status: DelegationStatus =
    (data as DelegationEdgeData | undefined)?.status ?? 'in_progress';

  const [path] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    curvature: 0.25,
  });

  // Per-status visual tuning. Live in-progress chains pulse against
  // the background; finished chains fade back to a quiet trace; errors
  // pop in rose so they're noticeable without being visually loud.
  let stroke: string;
  let opacity: number;
  switch (status) {
    case 'complete':
      stroke = 'var(--ws-delegation, #a855f7)';
      opacity = 0.5;
      break;
    case 'error':
      stroke = 'var(--ws-delegation-error, #f43f5e)';
      opacity = 0.9;
      break;
    case 'in_progress':
    default:
      stroke = 'var(--ws-delegation, #a855f7)';
      opacity = 0.9;
      break;
  }

  // The static path uses `BaseEdge` so hover hit-area + marker
  // semantics match the rest of the canvas. The animated overlay is
  // a plain raw `<path>` underneath the marker so the dot motion is
  // never clipped by the arrowhead.
  const isInProgress = status === 'in_progress';
  return (
    <g className="ws-delegation-edge" data-status={status}>
      <BaseEdge
        id={id}
        path={path}
        style={{
          ...(style ?? {}),
          stroke,
          strokeWidth: 1.5,
          // While dots are travelling overhead, drop the base path to
          // a quiet guide line so the motion reads as the dominant
          // signal. Once the chain finishes the base edge takes back
          // its full presence.
          opacity: isInProgress ? 0.25 : opacity,
        }}
        markerEnd={markerEnd}
      />
      {isInProgress ? (
        <path
          d={path}
          fill="none"
          stroke={stroke}
          // `linecap: round` + a `0 <gap>` dash array collapses each
          // dash to a single round dot. Keep the gap in sync with the
          // `stroke-dashoffset` keyframe end value in globals.css
          // (currently -72) or the dots will visibly slide out of
          // step at the wrap-around.
          //
          // Style intent: a couple of glowing beads of light tracing
          // the path — wide spacing (72px) so only a handful are on
          // screen at once, plus a CSS-driven glow filter + a gentle
          // stroke-width pulse give each dot a magical, breathing
          // quality. Base stroke-width is intentionally LOW (matching
          // the keyframe minimum) — the keyframe expands it on each
          // pulse, never the other way round.
          strokeWidth={2.5}
          strokeDasharray="0 72"
          strokeLinecap="round"
          className="ws-delegation-edge-flow"
          pointerEvents="none"
        />
      ) : null}
    </g>
  );
}

export const DelegationEdge = memo(DelegationEdgeImpl);
